import React from "react";
import "./styles.css";

const principles = [
  {
    number: "01",
    eyebrow: "BUILD YOUR FOUNDATION",
    title: "The small supports the big.",
    description:
      "Your bigger movements depend on smaller muscles doing their job. Stabilizers help control movement and support your joints. Build the foundation, and give every movement a stronger base.",
    takeaway: "Stability before strength.",
  },
  {
    number: "02",
    eyebrow: "TRAIN FOR REAL LIFE",
    title: "Movement goes beyond the gym.",
    description:
      "Your training shouldn't stop mattering when you leave the gym. We believe the strength, control, and mobility you develop should translate into everyday life—and everyday movement should inspire how you train.",
    takeaway: "Train for life, not just reps.",
  },
  {
    number: "03",
    eyebrow: "SHOW UP. TRACK IT.",
    title: "Today's habits shape tomorrow's you.",
    description:
      "Progress is built through the things you do repeatedly. Every session, every habit, every small improvement adds up. Tracking helps you see where you started, what you've done, and where you're going.",
    takeaway: "Consistency creates progress.",
  },
];

const periodization = [
  {
    number: "01",
    eyebrow: "THE PATH",
    title: "A clear path forward.",
    description:
      "Periodization is the systematic, cyclical planning of training. You know what this block is for, and you know what comes next.",
    takeaway: "Systematic. Cyclical. Clear.",
  },
  {
    number: "02",
    eyebrow: "THE DOING",
    title: "Execution becomes simple.",
    description:
      "When you are disciplined with the planning, the actual doing gets simple. Show up and train the session in front of you.",
    takeaway: "Plan it. Then do it.",
  },
  {
    number: "03",
    eyebrow: "THE COMPOUND",
    title: "Strength stacks on strength.",
    description:
      "Each cycle lays strength on top of the last. Start slow, stay consistent, and the work compounds until you are stronger than where you began.",
    takeaway: "Slow, consistent, stronger.",
  },
];

const features = [
  {
    number: "01",
    title: "No unnecessary questions",
    description:
      "You know your body better than we do. Skip the fluff and get straight to your training.",
    tag: "YOUR BODY. YOUR CALL.",
  },
  {
    number: "02",
    title: "Log as you go",
    description:
      "Add workouts directly to your day. Your workout log should work around you, not the other way around.",
    tag: "LESS FRICTION",
  },
  {
    number: "03",
    title: "Plan your week",
    description:
      "Organise your training in advance. Periodization only works when every session has a place in the cycle.",
    tag: "STAY PREPARED",
  },
  {
    number: "04",
    title: "Train the smaller muscles",
    description:
      "Search for stabilizer-focused exercises and give the supporting muscles the attention they deserve.",
    tag: "BUILD YOUR BASE",
  },
  {
    number: "05",
    title: "Make HIIT your own",
    description: "Create and plan your own HIIT workouts with Muscle Up PRO.",
    tag: "PRO FEATURE",
  },
  {
    number: "06",
    title: "Plans built around your goals",
    description:
      "Get AI-generated workout plans tailored to your specific fitness goals.",
    tag: "SMARTER PLANNING",
  },
];

const PhilosophySection = () => {
  return (
    <section
      className="home-philosophy"
      id="philosophy"
      tabIndex={-1}
      aria-labelledby="philosophy-title"
    >
      <div className="philosophy-wrap philosophy-intro">
        <p className="philosophy-kicker">
          <span className="home-dot" />
          OUR PHILOSOPHY
        </p>
        <div className="philosophy-intro-copy">
          <h2 id="philosophy-title">
            Fitness is built
            <br />
            <span>one day at a time.</span>
          </h2>
          <p>
            No unnecessary complexity. No distractions. Just your workouts, your
            habits, and a clearer picture of your progress.
          </p>
        </div>
      </div>

      <div className="philosophy-wrap philosophy-standfor">
        <div className="philosophy-label">
          <span>01</span>
          WHAT WE STAND FOR
        </div>
        <div>
          <h3>
            Three principles.
            <br />
            <span>One stronger you.</span>
          </h3>
          <p>
            We believe fitness works best when you understand the fundamentals,
            move with purpose, and stay consistent enough to see the difference.
          </p>
        </div>
      </div>

      <div className="philosophy-wrap philosophy-principles">
        {principles.map((principle) => (
          <article className="philosophy-card" key={principle.number}>
            <span className="philosophy-card-number">{principle.number}</span>
            <span className="philosophy-card-eyebrow">{principle.eyebrow}</span>
            <h3>{principle.title}</h3>
            <p>{principle.description}</p>
            <div className="philosophy-takeaway">
              <span aria-hidden="true">✓</span>
              {principle.takeaway}
            </div>
          </article>
        ))}
      </div>

      <div className="philosophy-wrap philosophy-standfor philosophy-period">
        <div className="philosophy-label">
          <span>02</span>
          PERIODIZATION
        </div>
        <div>
          <h3>
            Plan the cycle.
            <br />
            <span>Strength compounds.</span>
          </h3>
          <p>
            Periodization — the systematic, cyclical planning of training —
            gives you a clear path forward. Muscle Up is built around it. When
            the plan is disciplined, the doing becomes simple, and each cycle
            layers strength on the last.
          </p>
        </div>
      </div>

      <div
        className="philosophy-wrap philosophy-principles"
        aria-label="How periodization works"
      >
        {periodization.map((principle) => (
          <article className="philosophy-card" key={principle.eyebrow}>
            <span className="philosophy-card-number">{principle.number}</span>
            <span className="philosophy-card-eyebrow">{principle.eyebrow}</span>
            <h3>{principle.title}</h3>
            <p>{principle.description}</p>
            <div className="philosophy-takeaway">
              <span aria-hidden="true">✓</span>
              {principle.takeaway}
            </div>
          </article>
        ))}
      </div>

      <div className="philosophy-bridge">
        <p className="philosophy-kicker philosophy-kicker-center">
          THAT&apos;S WHY WE BUILT THIS
        </p>
        <h2>
          Your training.
          <br />
          <span>Your rules.</span>
        </h2>
        <p>
          Muscle Up is built around periodization. Plan the cycle, do the work,
          and let strength compound — without fighting your tools to stay on
          the path.
        </p>
        <div className="philosophy-bridge-rule">
          <span />
          LESS NOISE. MORE ACTION.
          <span />
        </div>
      </div>

      <div className="philosophy-wrap philosophy-features">
        <div className="philosophy-label">
          <span>03</span>
          THE MUSCLE UP APPROACH
        </div>
        <div className="philosophy-features-head">
          <h2>
            Everything you need.
            <br />
            <span>Nothing you don&apos;t.</span>
          </h2>
          <p>
            Your fitness journey is personal. Your workout logger should be,
            too.
          </p>
        </div>
        <div className="philosophy-feature-grid">
          {features.map((feature) => (
            <article className="philosophy-feature" key={feature.number}>
              <div className="philosophy-feature-top">
                <b>{feature.number}</b>
                <span>{feature.tag}</span>
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="philosophy-close">
        <div className="philosophy-close-copy">
          <p className="philosophy-kicker">
            <span className="home-dot" />
            YOUR JOURNEY STARTS HERE
          </p>
          <h2>
            Start slow.
            <br />
            <span>Stay consistent.</span>
            <br />
            Track your progress.
          </h2>
          <p>
            We&apos;ve put together practical fitness guidelines drawn from our
            years of lifting and staying active. Start slow, stay consistent,
            and let each cycle layer strength on the last. The work compounds.
          </p>
          <div className="philosophy-mantra">
            <span>BUILD YOUR FOUNDATION.</span>
            <span>OWN YOUR MOVEMENT.</span>
            <span>LET IT COMPOUND.</span>
            <span>MUSCLE UP.</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;
